<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '../stores/notifications'
import type { PosNotification } from '../stores/notifications'

/**
 * Full-page notifications tab. Tabs: All / Unread / by category.
 *
 * Reuses the same Pinia store that powers the header bell and drawer —
 * everything stays in sync (mark-read updates the bell, polling appends
 * here). Click a row → mark read + deep-link to the linked record.
 */

type TabKey = 'all' | 'unread' | string

const store = useNotificationsStore()
const router = useRouter()

const activeTab = ref<TabKey>('all')

onMounted(async () => {
  if (!store.initialLoaded) {
    await store.load({ limit: 60 })
  }
})

const categories = computed(() => {
  const set = new Set<string>()
  for (const n of store.items) {
    if (n.category) set.add(n.category)
  }
  return Array.from(set).sort()
})

const filtered = computed<PosNotification[]>(() => {
  const visible = store.items.filter((n) => !n.dismissed_at)
  if (activeTab.value === 'all') return visible
  if (activeTab.value === 'unread') return visible.filter((n) => !n.read_at)
  return visible.filter((n) => n.category === activeTab.value)
})

const groupedByDay = computed(() => {
  const buckets = new Map<string, PosNotification[]>()
  for (const n of filtered.value) {
    const key = dayLabel(n.created_at)
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key)!.push(n)
  }
  return Array.from(buckets.entries())
})

function dayLabel(iso: string | null): string {
  if (!iso) return 'Earlier'
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return 'Earlier'
  const today = new Date()
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (same(t, today)) return 'Today'
  if (same(t, yesterday)) return 'Yesterday'
  return t.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function severityClass(severity: string): string {
  switch (severity) {
    case 'urgent':
    case 'error':
      return 'bg-red-100 text-red-700 border-red-300'
    case 'warning':
      return 'bg-amber-100 text-amber-700 border-amber-300'
    case 'success':
      return 'bg-emerald-100 text-emerald-700 border-emerald-300'
    default:
      return 'bg-blue-100 text-blue-700 border-blue-300'
  }
}

function timeOnly(iso: string | null): string {
  if (!iso) return ''
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return ''
  return t.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

async function openNotification(n: PosNotification) {
  if (!n.read_at) await store.markRead(n.id)
  if (!n.link) return
  // Deep-link convention: `sales-order:UID` opens the order in a modal on
  // the POS Screen. Pre-set the ref so POSListView's `immediate: true`
  // watcher fires the moment the route mounts; clicking from any
  // back-office surface (including this full-page tab) lands the cashier
  // on /pos with the order modal already open.
  if (n.link.startsWith('sales-order:')) {
    const uid = n.link.slice('sales-order:'.length)
    store.openSalesOrder(uid)
    if (!router.currentRoute.value.path.startsWith('/pos')) {
      await router.push('/pos')
    }
    return
  }
  router.push(n.link)
}

async function refresh() {
  await store.load({ limit: 60 })
}
</script>

<template>
  <div class="max-w-5xl mx-auto p-4 md:p-6">
    <header class="mb-4 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Notifications</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          <span v-if="store.unreadCount > 0" class="text-red-500 font-medium">
            {{ store.unreadCount }} unread
          </span>
          <span v-else>All caught up — no unread notifications.</span>
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="refresh"
          :disabled="store.loading"
          class="text-sm px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {{ store.loading ? 'Refreshing…' : 'Refresh' }}
        </button>
        <button
          v-if="store.unreadCount > 0"
          @click="store.markAllRead"
          class="text-sm px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          Mark all read
        </button>
        <button
          v-if="filtered.length > 0"
          @click="store.dismissAll"
          class="text-sm px-3 py-2 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
          title="Dismiss every notification (read or unread)"
        >
          Clear all
        </button>
      </div>
    </header>

    <!-- Tabs -->
    <nav class="border-b border-gray-200 dark:border-gray-800 mb-4">
      <ul class="flex flex-wrap gap-1 -mb-px">
        <li>
          <button
            @click="activeTab = 'all'"
            class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
            :class="activeTab === 'all'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-white'"
          >
            All
            <span class="ml-1 text-xs text-gray-400">({{ store.items.filter((n) => !n.dismissed_at).length }})</span>
          </button>
        </li>
        <li>
          <button
            @click="activeTab = 'unread'"
            class="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
            :class="activeTab === 'unread'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-white'"
          >
            Unread
            <span class="ml-1 text-xs font-semibold text-red-500" v-if="store.unreadCount > 0">
              ({{ store.unreadCount }})
            </span>
          </button>
        </li>
        <li v-for="cat in categories" :key="cat">
          <button
            @click="activeTab = cat"
            class="px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize"
            :class="activeTab === cat
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-white'"
          >
            {{ cat }}
          </button>
        </li>
      </ul>
    </nav>

    <!-- Empty / loading state -->
    <div
      v-if="store.loading && store.items.length === 0"
      class="p-12 text-center text-gray-500 dark:text-gray-400"
    >
      Loading notifications…
    </div>

    <div
      v-else-if="filtered.length === 0"
      class="p-12 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800"
    >
      <div class="text-4xl mb-2">🔔</div>
      <div class="font-medium text-gray-700 dark:text-gray-300">No notifications here</div>
      <div class="text-sm mt-1">
        <span v-if="activeTab === 'unread'">You're all caught up.</span>
        <span v-else>You'll see updates here when something happens.</span>
      </div>
    </div>

    <!-- Grouped list -->
    <div v-else class="space-y-6">
      <section v-for="[day, rows] in groupedByDay" :key="day">
        <h2 class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2 px-1">
          {{ day }}
        </h2>
        <ul class="bg-white dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
          <li
            v-for="n in rows"
            :key="n.id"
            @click="openNotification(n)"
            class="p-4 cursor-pointer transition-colors"
            :class="!n.read_at
              ? 'bg-blue-50/30 dark:bg-blue-950/10 hover:bg-blue-50 dark:hover:bg-blue-950/30'
              : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'"
          >
            <div class="flex items-start gap-3">
              <span
                class="inline-block w-2.5 h-2.5 mt-2 rounded-full flex-shrink-0"
                :class="!n.read_at ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'"
              />
              <div class="flex-1 min-w-0">
                <div class="flex flex-wrap items-center gap-2 mb-1">
                  <span
                    v-if="n.category"
                    class="text-[10px] px-1.5 py-0.5 rounded-full border uppercase tracking-wide"
                    :class="severityClass(n.severity)"
                  >
                    {{ n.category }}
                  </span>
                  <span
                    v-if="n.severity && n.severity !== 'info'"
                    class="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 uppercase tracking-wide"
                  >
                    {{ n.severity }}
                  </span>
                  <span class="text-xs text-gray-400 ml-auto">{{ timeOnly(n.created_at) }}</span>
                </div>
                <h3
                  class="text-gray-800 dark:text-white text-sm leading-snug"
                  :class="!n.read_at ? 'font-semibold' : 'font-medium'"
                >
                  {{ n.title }}
                </h3>
                <p v-if="n.body" class="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  {{ n.body }}
                </p>
                <div v-if="n.record_type" class="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                  Linked: {{ n.record_type.split('\\').pop() }}#{{ n.record_id }}
                </div>
              </div>
              <div class="flex flex-col gap-1 items-end">
                <button
                  v-if="!n.read_at"
                  @click.stop="store.markRead(n.id)"
                  class="text-xs text-blue-600 hover:underline"
                  title="Mark as read"
                >
                  Read
                </button>
                <button
                  @click.stop="store.dismiss(n.id)"
                  class="text-xs text-gray-400 hover:text-red-500"
                  title="Dismiss"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>
